<template>
  <b-modal id="refund-kiosk-modal">
    <div class="mb-3">
      <p>
        Please adjust the refund amounts for each kiosk item below using the plus and minus buttons.
        You cannot refund more items than have been purchased.
      </p>
      <p>
        Alternatively, select <strong>Full Refund</strong> to return the entire amount paid for each kiosk item.
      </p>
      <b-form-checkbox id="full-refund-switch" v-model="fullRefund" switch>
        Full Refund
      </b-form-checkbox>
    </div>

    <b-list-group v-if="!fullRefund">
      <b-list-group-item
        v-for="item in refundItems"
        :key="item.kiosk_item_id"
      >
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-1">{{ item.name }}</h5>
            <small class="text-muted">
              Price per item: £{{ item.price.toFixed(2) }}
            </small>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              :disabled="item.refundAmount <= 0 || submitting"
              size="sm"
              variant="secondary"
              @click="decrement(item)"
            >
              -
            </b-button>
            <span class="mx-2">{{ item.refundAmount }}</span>
            <b-button
              :disabled="item.refundAmount >= item.amount || submitting"
              size="sm"
              variant="secondary"
              @click="increment(item)"
            >
              +
            </b-button>
          </div>
        </div>
      </b-list-group-item>
    </b-list-group>

    <div class="mt-3">
      <b-form-group label="Reason for refund:" label-for="refund-reason">
        <b-form-textarea
          id="refund-reason"
          v-model="refundReason"
          placeholder="Enter a reason for the refund..."
          rows="3"
        />
      </b-form-group>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" :disabled="submitting" @click="$emit('close')">
        Cancel
      </b-button>
      <b-button variant="primary" :disabled="disableRefundButton" @click="refundSocialKioskItems">
        Refund Items
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import VisitService from '@/services/VisitService';

export default {
  name: 'RefundKioskItemsModal',
  props: {
    callId: {
      type: String,
      required: true,
    },
    kioskItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      refundItems: [],
      refundReason: '',
      fullRefund: false,
    };
  },
  computed: {
    disableRefundButton() {
      if (this.submitting) return true;
      if (this.fullRefund) return false;

      const hasRefundSelection = this.refundItems.some(item => item.refundAmount > 0);
      return !hasRefundSelection;
    },
  },
  mounted() {
    this.refundItems = this.kioskItems.map(item => ({
      kiosk_item_id: item.kiosk_item_id,
      name: item.name,
      price: item.price,
      amount: item.amount,
      // Local field for user-selected refund amount.
      refundAmount: 0,
    }));
  },
  methods: {
    increment(item) {
      if (item.refundAmount < item.amount) {
        this.$set(item, 'refundAmount', item.refundAmount + 1);
      }
    },
    decrement(item) {
      if (item.refundAmount > 0) {
        this.$set(item, 'refundAmount', item.refundAmount - 1);
      }
    },
    async refundSocialKioskItems() {
      try {
        this.submitting = true;

        let itemsToRefund = this.refundItems;

        if (this.fullRefund) {
          itemsToRefund = this.refundItems.map(item => ({
            ...item,
            refundAmount: item.amount,
          }));
        }

        const payload = {
          kiosk_items: itemsToRefund
            .filter(item => item.refundAmount > 0)
            .map(item => ({
              kiosk_item_id: item.kiosk_item_id,
              amount_refunded: item.refundAmount,
            })),
          reason: this.refundReason,
        };

        await VisitService.refundKioskItems(this.callId, payload);

        await this.$store.dispatch('callDetails/getCall', this.callId);

        this.$toast.success('Refunded kiosk items successfully');
        this.$emit('close');
      } catch (e) {
        console.error('Error refunding kiosk items: ', e);
        this.$toast.error('Error refunding kiosk items: ', e);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
